import { LitElement, html, css } from 'lit';

export class CrusherTextarea extends LitElement {
  static formAssociated = true;

  static styles = css`
    :host { display: block; }
    .form-item { margin-bottom: var(--crusher-textarea-margin-bottom, var(--crusher-spacing-4)); }
    .label {
      display: block; font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm); color: var(--crusher-fg-muted);
      margin-bottom: var(--crusher-spacing-2);
    }
    .textarea {
      display: block;
      width: 100%;
      height: var(--crusher-textarea-height, auto);
      padding: var(--crusher-textarea-padding, var(--crusher-spacing-3) var(--crusher-spacing-4));
      font-size: var(--crusher-textarea-font-size, var(--crusher-font-size-base));
      font-family: var(--crusher-textarea-font-family, var(--crusher-font-family-body));
      color: var(--crusher-textarea-color, var(--crusher-fg));
      background: var(--crusher-textarea-bg, var(--crusher-surface));
      border: var(--crusher-textarea-border-width, var(--crusher-component-border-weight)) solid var(--crusher-textarea-border, var(--crusher-border));
      border-radius: var(--crusher-textarea-radius, var(--crusher-radius-md));
      cursor: inherit;
      transition: all var(--crusher-motion-duration-base) var(--crusher-motion-easing-inout);
      box-sizing: border-box;
      resize: var(--crusher-textarea-resize, vertical);
    }
    .textarea:focus {
      outline: var(--crusher-textarea-focus-outline, var(--crusher-focus-width) solid var(--crusher-color-brand-primary));
      outline-offset: var(--crusher-textarea-focus-outline-offset, var(--crusher-focus-offset));
      border-color: var(--crusher-textarea-focus-border, transparent);
      box-shadow: var(--crusher-textarea-focus-shadow, none);
    }
    .textarea::placeholder { color: var(--crusher-fg-muted); opacity: var(--crusher-opacity-muted); }
    .textarea[aria-invalid="true"] { border-color: color-mix(in srgb, var(--crusher-feedback-danger) 70%, transparent); }
  `;

  static properties = {
    label: { type: String },
    placeholder: { type: String },
    value: { type: String },
    rows: { type: Number },
    name: { type: String, reflect: true },
    autocomplete: { type: String },
    minlength: { type: Number },
    maxlength: { type: Number },
    required: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    invalid: { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.label = 'Textarea';
    this.placeholder = '';
    this.value = '';
    this.rows = 4;
    this.name = '';
    this.autocomplete = '';
    this.minlength = undefined;
    this.maxlength = undefined;
    this.required = false;
    this.disabled = false;
    this.invalid = false;
    this._internals = this.attachInternals?.();
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('tabindex')) this.tabIndex = -1;
  }

  focus() { this.renderRoot?.getElementById('textarea')?.focus(); }

  firstUpdated() {
    this._syncFormState();
  }

  updated() {
    this._syncFormState();
  }

  _validityFlags(textarea) {
    return textarea.validity.valid ? {} : {
      valueMissing: textarea.validity.valueMissing,
      tooLong: textarea.validity.tooLong,
      tooShort: textarea.validity.tooShort,
      customError: textarea.validity.customError,
    };
  }

  _syncFormState() {
    const textarea = this.renderRoot?.getElementById('textarea');
    this._internals?.setFormValue(this.disabled ? null : this.value);
    if (!textarea || !this._internals?.setValidity) return;
    const flags = this._validityFlags(textarea);
    this._internals.setValidity(flags, textarea.validationMessage, textarea);
  }

  checkValidity() {
    return this._internals?.checkValidity?.() ?? this.renderRoot?.getElementById('textarea')?.checkValidity() ?? true;
  }

  reportValidity() {
    return this._internals?.reportValidity?.() ?? this.renderRoot?.getElementById('textarea')?.reportValidity() ?? true;
  }

  formResetCallback() {
    this.value = this.getAttribute('value') ?? '';
    this.invalid = false;
    this._syncFormState();
  }

  _onInput(e) {
    this.value = e.target.value;
    this.invalid = !e.target.validity.valid;
    this._syncFormState();
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="form-item">
        ${this.label ? html`<label class="label" for="textarea">${this.label}${this.required ? ' *' : ''}</label>` : null}
        <textarea
          class="textarea"
          id="textarea"
          .placeholder=${this.placeholder}
          .rows=${this.rows}
          autocomplete=${this.autocomplete || ''}
          minlength=${this.minlength ?? ''}
          maxlength=${this.maxlength ?? ''}
          ?required=${this.required}
          ?disabled=${this.disabled}
          aria-invalid=${this.invalid ? 'true' : 'false'}
          @input=${this._onInput}
          @change=${() => this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))}
          .value=${this.value}
        ></textarea>
      </div>
    `;
  }
}
customElements.define('crusher-textarea', CrusherTextarea);
